import { OverrideStorageConfig, StorageService } from './storage.service';

function createGetter(useStorage: StorageProvider, storageKey: string) {
  return function () {
    const value = useStorage().getItem(storageKey);
    return value;
  };
}
function createSetter(useStorage: StorageProvider, storageKey: string) {
  return function (value: unknown) {
    useStorage().setItem(storageKey, value);
  };
}

/** This is needed so we can lazy load the storage service to avoid a race condition with the app initializer */
type StorageProvider = () => StorageService;

export function PropertyStorage(
  storage: StorageService | StorageProvider,
  key?: string,
  config?: OverrideStorageConfig,
): PropertyDecorator {
  function storageProvider() {
    const useStorage = typeof storage === 'function' ? storage() : storage;
    if (config) {
      return useStorage.withConfig(config);
    }
    return useStorage;
  }
  return function (target: unknown, propertyKey: PropertyKey) {
    const descriptor = Object.getOwnPropertyDescriptor(target, propertyKey);
    const storageKey = key ?? (typeof propertyKey === 'string' || typeof propertyKey === 'number' ? '' + propertyKey : undefined);
    if (!storageKey) {
      throw new Error('A property that is defined as a symbol must have a storage key defined');
    }
    if (!descriptor) {
      Object.defineProperty(target, propertyKey, {
        configurable: true,
        enumerable: true,
        get: createGetter(storageProvider, storageKey),
        set: createSetter(storageProvider, storageKey),
      });
    } else {
      descriptor.get = createGetter(storageProvider, storageKey);
      descriptor.set = createSetter(storageProvider, storageKey);
    }
  };
}

export function SessionStorage(key?: string, config?: OverrideStorageConfig): PropertyDecorator {
  function storageProvider() {
    if (!StorageService.storages.has(sessionStorage)) {
      throw new Error('SessionStorage is not available. Please ensure that the StorageService is initialized with sessionStorage.');
    }
    return StorageService.storages.get(sessionStorage);
  }
  return PropertyStorage(storageProvider, key, config);
}
export function LocalStorage(key?: string, config?: OverrideStorageConfig): PropertyDecorator {
  function storageProvider() {
    if (!StorageService.storages.has(localStorage)) {
      throw new Error('LocalStorage is not available. Please ensure that the StorageService is initialized with localStorage.');
    }
    return StorageService.storages.get(localStorage);
  }
  return PropertyStorage(storageProvider, key, config);
}
